/**
 * Builder for defining user-actions
 */
import { SafeHtml } from '@angular/platform-browser';
import { MngColor } from '@mng/mng-icon';
import { MatMenu } from '@angular/material/menu';

export class MngActionDef {
  variant: 'basic' | 'raised' | 'flat' | 'stroked' = 'basic';
  _title: string;
  _titleHtmlString: string;
  _titleFunction: (arg?: any) => string;
  _iconName: string;
  _iconNameFunction: (arg?: any) => string;
  _iconColor: MngColor;
  _iconColorFunction: (arg?: any) => MngColor;
  _iconTemplate: string;
  _iconTemplateFunction: () => string;
  divider: boolean;
  action: (arg?: any, index?: number) => any;
  actionArg: any;
  width = '';
  target: string[];
  targetComparePaths: string[];
  externalUrl: string;
  externalUrlTarget: '_self' | '_blank';
  _id: string;
  menu: MngActionDef[];
  menuRef: MatMenu;
  visibleFunction: (arg?: any) => boolean;
  disabledFunction: (arg?: any) => boolean;
  _tooltip: string;
  _disabledTooltipFunc: (arg?: any) => string;
  _showTooltip: boolean;
  _color = 'accent';
  _colorFunction: (arg?: any) => string;
  dialog = false;
  dialogTitle: string;
  dialogIconName: string;
  dialogHtmlContent: string;
  dialogYesButtonText: string;
  dialogNoButtonText: string;
  hotkey: string;
  hiddenInSomePlace: boolean;
  group?: string;
  additionalProps: { [key: string]: any } = {};

  withHiddenInSomePlace(hidden = true): MngActionDef {
    this.hiddenInSomePlace = hidden;
    return this;
  }

  withMenu(actionDefs: MngActionDef[]): MngActionDef {
    this.menu = actionDefs;
    return this;
  }

  withMenuRef(menuRef: MatMenu): MngActionDef {
    this.menuRef = menuRef;
    return this;
  }

  // function must be passed with .bind(this)
  withVisibleFunction(visibleFunc: (arg?: any) => boolean): MngActionDef {
    this.visibleFunction = visibleFunc;
    return this;
  }

  withTitle(title: string): MngActionDef {
    this._title = title;
    return this;
  }

  withTitleHtmlString(htlmString: string): MngActionDef {
    this._titleHtmlString = htlmString;
    return this;
  }

  withWidth(width: string): MngActionDef {
    this.width = width;
    return this;
  }

  withTitleFunction(titleFunc: (arg?: any) => string): MngActionDef {
    this._titleFunction = titleFunc;
    return this;
  }

  withGroup(group: string): this {
    this.group = group;
    return this;
  }

  withRaised(): MngActionDef {
    this.variant = 'raised';
    return this;
  }

  withFlat(): MngActionDef {
    this.variant = 'flat';
    return this;
  }

  withStroked(): MngActionDef {
    this.variant = 'stroked';
    return this;
  }

  withTooltip(text?: string): MngActionDef {
    if (text) {
      this._tooltip = text;
    }
    this._showTooltip = true;
    return this;
  }

  withDisabled(disabledFunc: (arg?: any) => boolean, disabledTooltipFunc?: (arg?: any) => string): MngActionDef {
    this.disabledFunction = disabledFunc;

    if (disabledTooltipFunc) {
      this._disabledTooltipFunc = disabledTooltipFunc;
    }
    return this;
  }

  withColor(color: string): MngActionDef {
    this._color = color;
    return this;
  }

  withColorFunction(colorFunc: (arg?: any) => string): MngActionDef {
    this._colorFunction = colorFunc;
    return this;
  }

  withIconName(iconName: string): MngActionDef {
    this._iconName = iconName;
    return this;
  }

  withIconNameFunc(iconNameFunc: (arg?: any) => string): MngActionDef {
    this._iconNameFunction = iconNameFunc;
    return this;
  }

  withIconColor(iconColor: MngColor): MngActionDef {
    this._iconColor = iconColor;
    return this;
  }

  withIconColorFunction(iconColorFunc: (arg?: any) => MngColor): MngActionDef {
    this._iconColorFunction = iconColorFunc;
    return this;
  }

  withDivider(): MngActionDef {
    this.divider = true;
    return this;
  }

  /**
   * @param actionFunc - Function to be executed by the action
   * @returns An ActionDef with a Function to be called
   */
  withAction(actionFunc: (arg?: any) => any): MngActionDef {
    this.action = actionFunc;
    return this;
  }

  with100PercentWidth(): MngActionDef {
    this.width = '100%';
    return this;
  }

  withTargetData(targetData: string[]): MngActionDef {
    this.target = targetData;
    return this;
  }

  withTarget(target: string): MngActionDef {
    this.target = [target];
    return this;
  }

  withTargetComparePaths(pathsToCompare: string[]): MngActionDef {
    this.targetComparePaths = pathsToCompare;
    return this;
  }

  withExternalTarget(url: string, target: '_self' | '_blank' = '_self'): MngActionDef {
    this.externalUrl = url;
    this.externalUrlTarget = target;
    return this;
  }

  withId(id: string): MngActionDef {
    this._id = id;
    return this;
  }

  /**
   *
   * @param title - Titel des Dialog
   * @param htmlContent - Inhalt des Dialogs als HTML-String
   * @param yesButtonText - Label des Ja-Button
   * @param noButtonText - Label des Nein-Button
   */
  withDialog(
    title: string,
    htmlContent: string,
    yesButtonText?: string,
    noButtonText?: string,
    iconName?: string
  ): MngActionDef {
    this.dialog = true;
    this.dialogTitle = title;
    this.dialogIconName = iconName;
    this.dialogHtmlContent = htmlContent;
    this.dialogYesButtonText = yesButtonText;
    this.dialogNoButtonText = noButtonText;

    return this;
  }

  /**
   *
   * @param hotkey - Keyboard Shortcut/Hotkey für die Action (eg. 'alt+s')
   */
  withHotkey(hotkey: string): MngActionDef {
    this.hotkey = hotkey;
    return this;
  }

  withAdditionalProps(props: { [key: string]: any }): MngActionDef {
    this.additionalProps = { ...this.additionalProps, ...props };
    return this;
  }

  getTitle(arg?: any) {
    if (this._titleFunction && arg !== undefined && arg !== null) {
      return this._titleFunction(arg);
    }
    if (this._titleFunction) {
      return this._titleFunction();
    }
    return this._title;
  }

  getText(arg?: any): string {
    if (this._titleHtmlString) {
      return this._titleHtmlString;
    }

    return this.getTitle(arg);
  }

  getIconName(arg?: any): string {
    return this._iconNameFunction && arg !== undefined && arg !== null
      ? this._iconNameFunction(arg)
      : this._iconNameFunction
        ? this._iconNameFunction()
        : this._iconName;
  }

  getColor(arg?: any): string {
    return this._colorFunction && arg !== undefined && arg !== null
      ? this._colorFunction(arg)
      : this._colorFunction
        ? this._colorFunction()
        : this._color;
  }

  getIconColor(arg?: any): MngColor {
    return this._iconColorFunction && arg !== undefined && arg !== null
      ? this._iconColorFunction(arg)
      : this._iconColorFunction
        ? this._iconColorFunction()
        : this._iconColor;
  }

  get iconTemplate(): SafeHtml {
    return this._iconTemplateFunction ? this._iconTemplateFunction() : this._iconTemplate;
  }

  get id(): string {
    return this._id ? this._id : this.getTitle() ? this.getTitle() : '';
  }

  get tooltip(): string {
    return this._tooltip ? this._tooltip : this.getTitle();
  }

  disabledTooltip(arg?: any): string {
    if (this._disabledTooltipFunc && arg !== undefined && arg !== null) {
      return this._disabledTooltipFunc(arg);
    }
    if (this._disabledTooltipFunc) {
      return this._disabledTooltipFunc();
    }
    return null;
  }

  isVisible(arg?: any): boolean {
    if (this.visibleFunction && arg !== undefined && arg !== null) {
      return this.visibleFunction(arg);
    }
    if (this.visibleFunction) {
      return this.visibleFunction();
    }
    return true;
  }

  isDisabled(arg?: any): boolean {
    if (this.disabledFunction && arg !== undefined && arg !== null) {
      return this.disabledFunction(arg);
    }
    if (this.disabledFunction) {
      return this.disabledFunction();
    }
    return false;
  }
}
